/**
 * No-op / duplicate / visibility classification — live-first, server-second.
 *
 * The client's live CRM store decides whether an action is a no-op WHILE it
 * has the record loaded; the server-computed `verdict.status === 'no_op'`
 * decides only while it does not (record not yet fetched, or genuinely
 * absent). `duplicate_of` is server-only and is never overridden by the live
 * read — the server is the only side that knows about a sibling action in
 * the same proposal.
 *
 * This is a restoration, not a new design: an earlier version of this module
 * detected no-ops purely against the live CRM store; a later rewrite
 * replaced it with the server `verdict` field and dropped the live path
 * entirely. Neither was superseded by the other — a proposal can sit for
 * days before review, during which the record it targets keeps changing, so
 * the server's verdict (computed once, at some point in the past) goes
 * stale exactly the way a proposal-time snapshot would. The live store is
 * the freshness layer over the server verdict, not a replacement for it: it
 * is unavailable for actions whose target hasn't loaded yet (or ever will,
 * for association targets — see `LiveRecordState`), which is exactly when
 * the server verdict is still needed as the fallback.
 *
 * Fail-open by design: an action whose `verdict` is absent (older cached
 * response, list path) AND whose live record is unloaded is treated as
 * visible + applicable, so it is never silently hidden from the user.
 */

import { extractValue } from '@/components/proposals/shared/utils';

import type { ProposedAction } from '../core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
  isPropertyUpdatePayload,
} from '../core/types';

/**
 * Normalize a value into a comparable string, or `null` when the value
 * provides no comparable data (fail open — never treated as equal).
 *
 * Trimming is used ONLY to detect whitespace-only strings (treated as empty →
 * `null`, fail open). Non-empty string contents are preserved verbatim: an
 * approval writes the untrimmed value, so " Timing " and "Timing" are a real
 * change and must NOT compare equal — trimming them to match would hide a
 * change the write would still persist.
 */
function toComparable(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim() === '' ? null : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

/**
 * Read-only view of a live CRM record, used to detect no-ops against the
 * record's current state rather than a proposal-time or verdict-time
 * snapshot.
 *
 * `hasRecord` gates whether the live methods below are consulted at all —
 * see {@link isFullyNoOpAction}. `getPropertyValue` / `getAssociationTargets`
 * return `undefined` to mean UNKNOWN — callers must fail open (never treat
 * an unknown current value/target set as a no-op).
 */
export interface LiveRecordState {
  /** Whether `entityId` is loaded and present in the live CRM store. */
  hasRecord(entityId: string): boolean;
  /** Current value of `propertyName` on `entityId`, or `undefined` if unknown. */
  getPropertyValue(entityId: string, propertyName: string): unknown | undefined;
  /**
   * Current association targets of `associationTypeName` on `entityId`, or
   * `undefined` if unknown.
   */
  getAssociationTargets(
    entityId: string,
    associationTypeName: string
  ): readonly string[] | undefined;
}

/**
 * Keys of `action`'s proposed properties whose value already equals the
 * record's CURRENT value in `live`. Non-property-update actions return `[]`.
 */
export function getNoOpPropertyKeysLive(
  action: ProposedAction,
  live: LiveRecordState
): string[] {
  if (!isPropertyUpdatePayload(action.payload)) {
    return [];
  }

  const { entity_id: entityId } = action.payload;
  const { properties } = action.payload.payload;

  return Object.keys(properties).filter(key => {
    const proposed = toComparable(extractValue(properties[key]));
    const current = toComparable(live.getPropertyValue(entityId, key));
    return current !== null && proposed !== null && current === proposed;
  });
}

/**
 * Whether `action` is fully a no-op against the live CRM record. Handles
 * both property updates and single associations:
 *
 * - `ahoy_property_updated`: every proposed property must already equal its
 *   current live value (see {@link getNoOpPropertyKeysLive}).
 * - `ahoy_association_added`: a no-op when the target is already among the
 *   record's current association targets of that type.
 * - `ahoy_association_removed`: a no-op when the target is NOT among the
 *   record's current association targets of that type.
 *
 * Fails open (`false`) whenever the live data needed for the comparison is
 * `undefined`, and for any other action type.
 */
export function isFullyNoOpActionLive(
  action: ProposedAction,
  live: LiveRecordState
): boolean {
  if (isPropertyUpdatePayload(action.payload)) {
    const keys = Object.keys(action.payload.payload.properties);
    if (keys.length === 0) {
      return false;
    }
    return getNoOpPropertyKeysLive(action, live).length === keys.length;
  }

  if (isAssociationAddedPayload(action.payload)) {
    const { entity_id: entityId, payload: associationPayload } = action.payload;
    const targets = live.getAssociationTargets(
      entityId,
      associationPayload.association_type_name
    );
    if (targets === undefined) {
      return false;
    }
    return targets.includes(associationPayload.target_entity_id);
  }

  if (isAssociationRemovedPayload(action.payload)) {
    const { entity_id: entityId, payload: associationPayload } = action.payload;
    const targets = live.getAssociationTargets(
      entityId,
      associationPayload.association_type_name
    );
    if (targets === undefined) {
      return false;
    }
    return !targets.includes(associationPayload.target_entity_id);
  }

  return false;
}

/**
 * IDs of the actions in `actions` that are fully no-op against `live` (see
 * {@link isFullyNoOpActionLive}).
 */
export function getNoOpActionIdsLive(
  actions: ProposedAction[],
  live: LiveRecordState
): number[] {
  return actions.filter(a => isFullyNoOpActionLive(a, live)).map(a => a.id);
}

/**
 * Whether an action classifies as fully no-op (would make no change if
 * approved), combining the server verdict with the live CRM store:
 *
 * - `duplicate_of` is server-only — the server is the only side that knows
 *   about the sibling action it duplicates. It is authoritative ONLY while the
 *   copy it duplicates is still selected: the static server verdict marks a
 *   later copy `duplicate_of` an earlier one, but if the user deselects that
 *   earlier copy it will not run, so THIS copy becomes the one that would
 *   create the edge — no longer a duplicate. When `isActionSelected` is
 *   supplied, the duplicate classification is recomputed from the CURRENT
 *   selection: a `duplicate_of` action is a no-op only while its
 *   `duplicate_of_action_id` is still selected; otherwise it falls through to
 *   the live/`no_op` checks below like any other action. When
 *   `isActionSelected` is omitted, `duplicate_of` is treated as a no-op
 *   unconditionally (selection-independent callers, e.g. the select-all set).
 * - For `ahoy_property_updated` / `ahoy_association_added` /
 *   `ahoy_association_removed`: while the record is loaded in the live
 *   store (`live.hasRecord`), the live read decides — including deciding
 *   NOT a no-op when the record has since changed, overriding a stale
 *   server `no_op`. While the record is not loaded, the server `no_op`
 *   verdict decides.
 * - Any other action type is never a no-op.
 *
 * Fail-open: absent verdict AND an unloaded live record → `false` (visible +
 * applicable).
 */
export function isFullyNoOpAction(
  action: ProposedAction,
  live: LiveRecordState,
  isActionSelected?: (actionId: number) => boolean
): boolean {
  if (action.verdict?.status === 'duplicate_of') {
    const sourceId = action.verdict.duplicate_of_action_id;
    // Selection-independent callers (isActionSelected omitted, e.g. the
    // select-all set) treat every duplicate_of as a no-op unconditionally.
    if (isActionSelected === undefined) {
      return true;
    }
    // Selection-aware: a duplicate is a no-op only while the copy it duplicates
    // is known AND still selected. Without a source id the duplicate
    // relationship is unconfirmed, and if the earlier copy is deselected this
    // copy becomes the one that would create the edge — either way fall through
    // to the live/no_op checks so it is shown and can still run.
    if (sourceId != null && isActionSelected(sourceId)) {
      return true;
    }
  }

  if (
    !isPropertyUpdatePayload(action.payload) &&
    !isAssociationAddedPayload(action.payload) &&
    !isAssociationRemovedPayload(action.payload)
  ) {
    return false;
  }

  const { entity_id: entityId } = action.payload;
  if (live.hasRecord(entityId)) {
    return isFullyNoOpActionLive(action, live);
  }
  return action.verdict?.status === 'no_op';
}

/**
 * IDs of actions classified as fully no-op by {@link isFullyNoOpAction}
 * (server `no_op`/`duplicate_of`, refined by the live store where loaded).
 * These are auto-deselected and hidden from the rendered list.
 *
 * Pass `isActionSelected` to recompute `duplicate_of` eligibility from the
 * current selection (see {@link isFullyNoOpAction}); omit it for a
 * selection-independent set.
 */
export function getNoOpActionIds(
  actions: ProposedAction[],
  live: LiveRecordState,
  isActionSelected?: (actionId: number) => boolean
): number[] {
  return actions
    .filter(a => isFullyNoOpAction(a, live, isActionSelected))
    .map(a => a.id);
}

/**
 * Actions with no-op actions (see {@link isFullyNoOpAction}) filtered out.
 */
export function getVisibleActions(
  actions: ProposedAction[],
  live: LiveRecordState,
  isActionSelected?: (actionId: number) => boolean
): ProposedAction[] {
  return actions.filter(a => !isFullyNoOpAction(a, live, isActionSelected));
}
